import PageContext from "@/components/Common/PageContext";
import { SearchBarContainer } from "@/components/Search/components/SearchBar/SearchBarContainer";
import { Container, List, ListItem, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { useContext, useState } from "react";
import { searchHelperText } from "../../components/Search/constants";
import { LoadingDialog } from "@/components/Loading/LoadingDialog";
import { ModalWithActionButton } from "@/components/Modal/ModalWithActionButton";
import { searchCreditRequests } from "@/services/apiHelper";
import PendingCreditRequestCard from "@/components/CreditRequest/PendingCreditRequestCard";
import { useStyles } from "@/components/ReceiveList/Receive.styles";
import { useHistory } from "react-router-dom";

interface CreditRequestSearchProps {
	pageTitle: string;
}

const useLocalStyles = makeStyles((theme) => ({
    title: {
        fontSize: "1.3rem",
        marginBottom: theme.spacing(1),
    },
}));

export default function CreditRequestSearch({pageTitle} : CreditRequestSearchProps) {
    const [pageContext] = useContext(PageContext);
    const { userLocation } = pageContext;
    const classes = useLocalStyles();
    const receiveClasses = useStyles();
    const history = useHistory();

    const [searchText, setSearchText] = useState("");
	const [searchResults, setSearchResults] = useState([]);
    const [isShowLoadingDialog, setIsShowLoadingDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isAlertSeverityError, setIsAlertSeverityError] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);

	const onSearchAsync = async (value: string) => {
		try {
			setIsShowLoadingDialog(true);
			const response = await searchCreditRequests(value, userLocation);
			setSearchText(value);
			setSearchResults((response).data);
			setIsShowLoadingDialog(false);
		} catch (error) {
			setIsAlertSeverityError(true);
			setSearchResults([]);
			setIsShowLoadingDialog(false);
			setErrorMessage(
				"Could not retrieve search results. Please try again and raise a support ticket if issue persists."
			);
			setShowErrorAlert(true);
		}
	};

    const handleCloseAlertAsync = async (event?: React.SyntheticEvent | MouseEvent, reason?: string) => {
        if (reason === "clickaway") {
            return;
        }
        setShowErrorAlert(false);
        setErrorMessage(null);
        setIsAlertSeverityError(false);
    };

    const onView = async (friendlyId: string) => {
		const matchingCreditRequest = searchResults.find((cr) => cr.friendlyId === friendlyId);
		history.push(`/creditrequest/detail?id=${matchingCreditRequest.id}`);
	};

    return (
        <Container disableGutters maxWidth="md">
            {pageTitle && (
				<Typography variant="h5" className={classes.title}>
					{pageTitle}
				</Typography>
			)}
			<SearchBarContainer
				handleSearch={onSearchAsync}
				helperText={searchHelperText.creditRequest}
			/>
			{!!searchText && (
				<Typography variant="body2" component="div">
					{searchResults.length} matching result(s) found
				</Typography>
			)}

            <List className={receiveClasses.creditRequestRootContainer}>
				{searchResults.map((creditRequest) => {
					const uniqueReasons = Array.from(
						new Set(
							creditRequest.lineItems
								.map((lineItem) => lineItem.reason)
						)
					);
					const reasons = uniqueReasons.join(", ");
					return (
						<ListItem key={creditRequest.id} className={receiveClasses.creditRequestListItem}>
							<PendingCreditRequestCard {...creditRequest} onView={onView} reason={reasons} lineStatus={creditRequest.status} />
						</ListItem>
					);
				})}
			</List>
            <LoadingDialog message="Retrieving results..." isOpen={isShowLoadingDialog} />
            <ModalWithActionButton
                title={isAlertSeverityError ? "Error" : "Warning"}
                alertType={isAlertSeverityError ? "error" : "warning"}
                primaryButtonName="Ok"
                onPrimaryButtonClick={() => handleCloseAlertAsync()}
                isOpen={showErrorAlert}
                message={errorMessage ?? ""}
            />
        </Container>
    )
} 